/**
 * ``useAlertDetail`` — TanStack Query hook around
 * ``GET /api/query/alert/{alert_id}/details`` (PR4; REQ-QUERY-025).
 *
 * Feeds the Entity Details panel's **Alert** sections (ALERT / LOCATION /
 * REGION / ASSOCIATIONS / SOURCE / DISMISSAL — REQ-UI-067) when an alert
 * is selected in the bus. The selection bus carries only the ``alert_id``;
 * the hook fetches the rest (the cross-sensor associated detections, file
 * attachments, and free-text additional information the list/dismiss shape
 * :class:`AlertItem` doesn't project).
 *
 * Mirrors :func:`entity-details/use-detection-detail` exactly — same
 * read-role gate, same ``404`` → ``null`` (no alert with that id / it aged
 * out — an empty state the sections render, **not** an error), and the same
 * no-retry-on-{401,403,404} predicate.
 *
 * Distinct from the dismiss MUTATION (:func:`alerts/use-dismiss-alert`):
 * this is the read surface the DISMISSAL section reads its state from; the
 * mutation is the write the section's Dismiss button fires.
 *
 * Cross-references:
 *   - REQ-QUERY-025 (Alert detail surface + dismissal) / REQ-AUTH-024.
 *   - REQ-UI-067 (Alert section schema).
 *   - ADR-029 / ADR-032 §4.2 (Alert data model — the nested-field source
 *     of truth).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type AlertDetail = components["schemas"]["AlertDetail"];

/** Same read-role gate as the other Entity Details endpoints (REQ-AUTH-024..027). */
export const ALERT_DETAIL_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** Alert *content* is immutable once recorded (only the dismissal columns
 * change, and those converge via the dismiss mutation + alert-dismissed
 * SSE); a longer stale time than the live status sections is fine. Mirrors
 * the Event detail hook. */
export const ALERT_DETAIL_STALE_TIME_MS = 60_000;

export function alertDetailQueryKey(alertId: string): readonly unknown[] {
  return ["alert-detail", alertId] as const;
}

export interface UseAlertDetailOptions {
  alertId: string | null;
}

export function useAlertDetail(
  options: UseAlertDetailOptions,
): UseQueryResult<AlertDetail | null, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(ALERT_DETAIL_READER_ROLES);
  const enabled = options.alertId !== null && hasRole;

  return useQuery<AlertDetail | null, ApiError>({
    queryKey:
      options.alertId !== null
        ? alertDetailQueryKey(options.alertId)
        : ["alert-detail", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.alertId === null) {
        throw new ApiError(0, null, "useAlertDetail: query ran without alertId");
      }
      const url = `/api/query/alert/${encodeURIComponent(options.alertId)}/details`;
      try {
        const raw = await apiFetch<unknown>(url, { signal });
        if (!isAlertDetail(raw)) {
          console.error(
            "[meridian/entity-details/use-alert-detail] response failed shape validation",
            raw,
          );
          throw new ApiError(500, raw, "alert-detail response did not match the expected shape");
        }
        return raw;
      } catch (err) {
        // 404 = no alert with that id → empty state, not error.
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled,
    staleTime: ALERT_DETAIL_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Minimal structural guard for an :type:`AlertDetail`. Validates the
 * always-present typed columns (the nested ``associated_detection[]`` /
 * ``associated_file[]`` arrays are optional, so they're not part of the
 * guard); a malformed payload that slips this guard surfaces as a 500 the
 * sections render as an error rather than rendering ``undefined`` fields.
 */
function isAlertDetail(value: unknown): value is AlertDetail {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.alert_id === "string" &&
    typeof obj.producer_id === "string" &&
    typeof obj.aggregator_id === "string" &&
    typeof obj.alert_type === "number" &&
    typeof obj.status === "number" &&
    typeof obj.priority === "number" &&
    typeof obj.message_hash === "string" &&
    typeof obj.schema_version === "number"
  );
}
